// WorkingHourController.js
import React, { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import { ConvertLocal12To24Hour, ConvertWorkingHoursToSameDateInTimeZone } from "../../util/workingHour";
import { useSelectedBusiness } from "../../redux/store";
import {
    EntityType,
    updateBusinessWorkingHour,
    updateStaffWorkingHour,
    updateWorkingHourGroup,
    WorkingHourOperation,
} from "../../redux/actions/businessActions";
import {
    ADD_WORKING_HOUR,
    DELETE_WORKING_HOUR,
    DELETE_WORKING_HOUR_OVERRIDE_GROUP,
    UPDATE_WORKING_HOUR,
    UPDATE_WORKING_HOUR_GROUP,
} from "../../graphql/mutations";
import { GET_WORKING_HOUR_OVERRIDE_GROUPS } from "../../graphql/queries";
import { useConfirm } from "material-ui-confirm";

export const WorkingHourType = {
    BASIC: "basic",
    OVERRIDE: "override",
    HOLIDAY: "holiday",
};

export const convertTimeToMinutes = (time) => {
    const period = time.slice(-2);
    const timePart = time.slice(0, -2).trim();
    const [hours, minutes] = timePart.split(":").map(Number);
    let totalMinutes = hours * 60 + minutes;
    if (period.toLowerCase() === "pm" && hours !== 12) {
        totalMinutes += 12 * 60;
    } else if (period.toLowerCase() === "am" && hours === 12) {
        totalMinutes -= 12 * 60;
    }
    return totalMinutes;
};

export const formatTimeWithPeriod = (timeInMinutes, initialPeriod) => {
    const totalMinutesInDay = 24 * 60;
    const normalizedMinutes = timeInMinutes % totalMinutesInDay;
    const hours24 = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    const period = hours24 >= 12 ? "pm" : "am";
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
};


export const useWorkingHourController = (props) => {
    const confirm = useConfirm();
    const [timeRangeErrors, setTimeRangeErrors] = useState([]);
    const [hasErrors, setHasErrors] = useState(false);
    const [updateWorkingHourData] = useMutation(UPDATE_WORKING_HOUR);
    const [deleteWorkingHourData] = useMutation(DELETE_WORKING_HOUR);
    const [addWorkingHourData] = useMutation(ADD_WORKING_HOUR);
    const [workingHourGroup, setWorkingHourGroup] = useState([]);
    const [deleteWorkingHourGroupData] = useMutation(DELETE_WORKING_HOUR_OVERRIDE_GROUP);
    const [updateWorkingHourGroupData] = useMutation(UPDATE_WORKING_HOUR_GROUP);

    const { data: workingHourOverrideGroups, refetch: refetchOverrides } = useQuery(
        GET_WORKING_HOUR_OVERRIDE_GROUPS,
        {
            variables: {
                staffId: Number(props?.staffData?.id),
        },
        skip: props.entityType !== EntityType.STAFF,
        }
    );

    const dispatch = useDispatch();
    const selectedBusiness = useSelectedBusiness();
    const selectedBusinessId = selectedBusiness?.businessBasic.id;

    const resolveStaffIdForGroup = useCallback(
        (groupId) => {
            const normalizedGroupId = Number(groupId);
            if (!normalizedGroupId) return Number(props?.providerUser?.staffId) || 0;
            const matchedStaff = (selectedBusiness?.businessStaffs || []).find((staff) =>
                (staff?.workingHourGroups || []).some((group) => Number(group?.id) === normalizedGroupId)
            );
            return Number(matchedStaff?.id) || Number(props?.providerUser?.staffId) || 0;
        },
        [selectedBusiness?.businessStaffs, props?.providerUser?.staffId]
    );

    const resolveTimeZoneContext = (selectedTimeZone) => {
        const tzObj =
            (selectedTimeZone && typeof selectedTimeZone === "object"
                ? selectedTimeZone
                : null) ||
            workingHourGroup?.timeZone ||
            props?.providerUser?.timeZone ||
            null;
        return {
            id: Number(tzObj?.id) || Number(props?.providerUser?.timeZone?.id) || null,
            timezone: tzObj?.timezone || props?.providerUser?.timeZone?.timezone || "UTC",
        };
    };

    useEffect(() => {
        setHasErrors(timeRangeErrors.length > 0);
    }, [timeRangeErrors]);

    const applyLocalWorkingHourChange = useCallback((updatedHours, operation) => {
        const normalizedHours = Array.isArray(updatedHours)
            ? updatedHours.filter(Boolean)
            : updatedHours
            ? [updatedHours]
            : [];
        if (!normalizedHours.length) return;

        setWorkingHourGroup((prev) => {
            if (!prev || !Array.isArray(prev.workingHours)) return prev;
            const nextHours = [...prev.workingHours];

            normalizedHours.forEach((incoming) => {
                const idx = nextHours.findIndex((row) => Number(row?.id) === Number(incoming?.id));

                if (operation === WorkingHourOperation.DELETE) {
                    if (idx !== -1) nextHours.splice(idx, 1);
                    return;
                }

                if (idx !== -1) {
                    nextHours[idx] = { ...nextHours[idx], ...incoming };
                } else {
                    nextHours.push(incoming);
                }
            });

            return { ...prev, workingHours: nextHours };
        });
    }, []);

    const normalizeWorkingHourRecord = useCallback((record, fallback) => {
        if (!record) return record;
        return {
            ...record,
            startTime: record.startTime || fallback?.startTime || "",
            endTime: record.endTime || fallback?.endTime || "",
        };
    }, []);

    const mergeWorkingHoursByIdWithFallback = useCallback((submittedRows, responseRows) => {
        const base = Array.isArray(submittedRows) ? submittedRows : [];
        const incoming = Array.isArray(responseRows) ? responseRows : [];
        if (!base.length && !incoming.length) return [];
        if (!incoming.length) return base;

        const structuralKey = (row) => {
            const parentId = row?.parentId === null || row?.parentId === undefined ? "null" : String(row.parentId);
            return [
                String(row?.groupId ?? ""),
                String(parentId),
                String(row?.day ?? ""),
                String(row?.startTime ?? ""),
                String(row?.endTime ?? ""),
                String(Boolean(row?.onJob)),
            ].join("|");
        };

        const responseById = new Map(
            incoming
                .filter((row) => Number.isFinite(Number(row?.id)))
                .map((row) => [Number(row.id), row])
        );
        const responseByStructure = new Map(
            incoming.map((row) => [structuralKey(row), row])
        );

        // Preserve submitted full payload, overlay backend updates where ids match.
        const merged = base.map((row) => {
            const id = Number(row?.id);
            const matchedById = Number.isFinite(id) ? responseById.get(id) : null;
            const matchedByStructure = responseByStructure.get(structuralKey(row));
            const matched = matchedById || matchedByStructure || null;
            if (!matched) return row;
            return normalizeWorkingHourRecord(
                {
                    ...row,
                    ...matched,
                },
                row
            );
        });

        // Include response rows that did not exist in submitted payload (by id and structure).
        const baseIds = new Set(
            base
                .filter((row) => Number.isFinite(Number(row?.id)))
                .map((row) => Number(row.id))
        );
        const baseStructures = new Set(base.map((row) => structuralKey(row)));
        incoming.forEach((row) => {
            const id = Number(row?.id);
            if ((!Number.isFinite(id) || !baseIds.has(id)) && !baseStructures.has(structuralKey(row))) {
                merged.push(normalizeWorkingHourRecord(row, {}));
            }
        });

        return merged;
    }, [normalizeWorkingHourRecord]);

    const handleUpdateWorkingHourGroup = useCallback(
        (workingHourGroup, workingHours, timezone) => {
            if (!workingHourGroup || !timezone) {
                console.warn("Missing workingHourGroup or timezone. Cannot update.");
                return;
            }

            updateWorkingHourGroupData({
                variables: {
                    input: {
                        timeZoneId: timezone.id,
                        workingHours: workingHours,
                    },
                    groupId: Number(workingHourGroup.id),
                },
            })
                .then((res) => {
                    // Extract and clean response data
                    let updateWorkingHourGroupResponse = res.data?.updateWorkingHourGroup;
                    if (!updateWorkingHourGroupResponse || typeof updateWorkingHourGroupResponse !== "object") {
                        return;
                    }
                    const { __typename, ...rest } = updateWorkingHourGroupResponse;

                    const mergedWorkingHours = mergeWorkingHoursByIdWithFallback(
                        workingHours,
                        Array.isArray(rest?.workingHours) ? rest.workingHours : []
                    );
                    const committedGroup = {
                        ...workingHourGroup,
                        ...rest,
                        workingHours: mergedWorkingHours.length ? mergedWorkingHours : workingHours,
                        timeZone: rest?.timeZone || timezone || workingHourGroup?.timeZone,
                    };

                    setWorkingHourGroup((prev) => {
                        if (!prev) return prev;
                        return committedGroup;
                    });
                    setTimeRangeErrors([]);
                    setHasErrors(false);

                    if (props.entityType === EntityType.STAFF) {
                        const staff = selectedBusiness?.businessStaffs.find(
                            (staff) => Number(staff.id) === props?.providerUser?.staffId
                        );
                        dispatch(updateWorkingHourGroup(staff.id, selectedBusinessId, committedGroup, WorkingHourOperation.UPDATE));
                    } else if (props.entityType === EntityType.BUSINESS) {
                        dispatch(updateWorkingHourGroup(null, selectedBusinessId, committedGroup, WorkingHourOperation.UPDATE));
                    }
                })
                .catch((error) => {
                    console.error("Error updating working hour:", error);
                });
        },
        [
            updateWorkingHourGroupData,
            props.entityType,
            props?.providerUser?.staffId,
            selectedBusiness?.businessStaffs,
            dispatch,
            selectedBusinessId,
            mergeWorkingHoursByIdWithFallback,
        ]
    );

    const handleTimezoneSelect = useCallback(
        (timezone) => {
            if (!timezone) {
                console.warn("No timezone selected.");
                return;
            }

            if (!workingHourGroup) {
                console.warn("No workingHourGroup available.");
                return;
            }

            // Check if the timezone has actually changed
            if (timezone.id === workingHourGroup.timeZone?.id) { 
                return;
            }
            const workingHoursArray = Object.values(workingHourGroup?.workingHours || {}); // Convert to array safely
            if (workingHoursArray.length === 0) {
                return;
            }
            const workingHoursWithoutTypename = workingHoursArray.map(({ __typename, ...rest }) => rest);

            const workingHours = ConvertWorkingHoursToSameDateInTimeZone(
                workingHoursWithoutTypename,
                workingHourGroup.timeZone,
                timezone
            );

            handleUpdateWorkingHourGroup(workingHourGroup, workingHours, timezone); // Pass the latest workingHourGroup
        },
        [workingHourGroup, handleUpdateWorkingHourGroup]
    );

    const showOrHidRow = (parent, tzContext) => {
        const tzObj = { timezone: tzContext?.timezone || "UTC" };
        const shouldRestoreDefaultWindow =
            !parent?.onJob &&
            (
                !String(parent?.startTime || "").trim() ||
                !String(parent?.endTime || "").trim()
            );
        const fallbackStartTime = shouldRestoreDefaultWindow ? "09:00 am" : parent?.startTime;
        const fallbackEndTime = shouldRestoreDefaultWindow ? "05:00 pm" : parent?.endTime;
        // Create a new object to avoid mutating the original parent object
        const updatedParent = {
            ...parent,
            onJob: !parent.onJob, // Toggle the onJob property  
            id: Number(parent.id), // Ensure id is Integer
            groupId: Number(parent.groupId), // Ensure groupId is Integer
            startTime: ConvertLocal12To24Hour(fallbackStartTime, tzObj) || fallbackStartTime,
            endTime: ConvertLocal12To24Hour(fallbackEndTime, tzObj) || fallbackEndTime,
        };

        const { __typename, ...inputWithoutTypename } = updatedParent;

        updateWorkingHourData({
            variables: { input: inputWithoutTypename, timeZoneId: tzContext?.id },
        })
            .then((res) => {
                let updatedWorkingHoursResponse = res.data?.updateWorkingHour;
                if (updatedWorkingHoursResponse && typeof updatedWorkingHoursResponse === "object") {
                    const { __typename, ...rest } = updatedWorkingHoursResponse;
                    updatedWorkingHoursResponse = normalizeWorkingHourRecord(
                        {
                            ...updatedParent,
                            ...rest,
                        },
                        updatedParent
                    );
                }

                applyLocalWorkingHourChange(updatedWorkingHoursResponse, WorkingHourOperation.UPDATE);
                setTimeRangeErrors([]);
                setHasErrors(false);
                // Dispatch updated working hours to the store
                updateWorkingHour(selectedBusinessId, updatedWorkingHoursResponse, WorkingHourOperation.UPDATE);
            })
            .catch((error) => {
                console.error("Error updating working hour:", error);
            });
    };

    const getWorkingHourOverrides = (isCustom, defaultWH) => {
        let workingHourGroup;

        if (props.entityType === EntityType.STAFF) {
            const staff = selectedBusiness?.businessStaffs.find(
                (staff) => parseInt(staff.id) === props?.providerUser?.staffId
            );
            workingHourGroup = staff?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.def === defaultWH
            );
        } else if (props.entityType === EntityType.BUSINESS) {
            workingHourGroup = selectedBusiness?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.isForBusiness === 1
            );
        }

        if (workingHourGroup) {
            setWorkingHourGroup(workingHourGroup);
        } else {
            setWorkingHourGroup([]);
        }
    };

    const getWorkingHourHolidays = (isCustom, defaultWH) => {
        let workingHourGroup;

        if (props.entityType === EntityType.STAFF) {
            const staff = selectedBusiness?.businessStaffs.find(
                (staff) => parseInt(staff.id) === props?.providerUser?.staffId
            );
            workingHourGroup = staff?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.def === defaultWH
            );
        } else if (props.entityType === EntityType.BUSINESS) {
            workingHourGroup = selectedBusiness?.workingHourGroups.find(
                (group) => group.custom === isCustom && group.isForBusiness === 1
            );
        }

        if (workingHourGroup) {
            setWorkingHourGroup(workingHourGroup);
        } else {
            setWorkingHourGroup([]);
        }
    };

    const getWorkingHours = () => {
        let workingHourGroup;

        if (props.entityType === EntityType.STAFF) {
            const staff = selectedBusiness?.businessStaffs.find(
                (staff) => parseInt(staff.id) === props?.providerUser?.staffId
            );
            if (props.groupId) {
                workingHourGroup = staff?.workingHourGroups.find((group) => Number(group.id) === Number(props.groupId));
            } else {
                workingHourGroup = staff?.workingHourGroups.find(
                    (group) => group.custom === props.isCustom && group.def === props.defaultWH
                );
            }
        } else if (props.entityType === EntityType.BUSINESS) {
            workingHourGroup = selectedBusiness?.workingHourGroups.find(
                (group) => group.custom === props.isCustom && group.isForBusiness === 1
            );
        }

        if (workingHourGroup) {
            setWorkingHourGroup(workingHourGroup);
        } else {
            setWorkingHourGroup([]);
        }
    };

    const getUpdatedWorkingHour = (updatedWorkingHour) => {
        if (!updatedWorkingHour) return null;

        const transformWorkingHour = (workingHour) => {
            const { __typename, ...rest } = workingHour;
            return {
                ...rest,
                startTime: rest.startTime,
                endTime: rest.endTime,
                id: parseInt(rest.id, 10),
                groupId: parseInt(rest.groupId, 10),
                parentId: rest.parentId !== null ? parseInt(rest.parentId, 10) : null,
                onJob: Boolean(rest.onJob),
            };
        };

        if (Array.isArray(updatedWorkingHour)) {
            return updatedWorkingHour.map(transformWorkingHour);
        } else {
            return transformWorkingHour(updatedWorkingHour);
        }
    };

    const handleChange = (parent, children, toBeUpdated, event, type, selectedTimeZone) => {
        const tzContext = resolveTimeZoneContext(selectedTimeZone);
        if (type === "onJob") {
            showOrHidRow(parent, tzContext);
            return;
        }

        const parsedTime = convert24HourTo12Hour(event.target.value, tzContext);

        if (parsedTime) {
            const toBeUpdatedWorkingHour = JSON.parse(JSON.stringify(toBeUpdated));
            if (type === "startTime") {
                toBeUpdatedWorkingHour.startTime = parsedTime;
            } else if (type === "endTime") {
                toBeUpdatedWorkingHour.endTime = parsedTime;
            } else {
                console.error("Error:", "Invalid type");
            }

            const updatedWorkingHour = getUpdatedWorkingHour(toBeUpdatedWorkingHour);
            const errors = handleValidate(parent, children, updatedWorkingHour, setTimeRangeErrors, setHasErrors);
            if (errors.length > 0) {
                console.error("Validation failed:", errors);
                return;
            }

            updateWorkingHourData({
                variables: { input: updatedWorkingHour, timeZoneId: tzContext.id },
            })
                .then((res) => {
                    const updatedWorkingHoursResponse = res.data?.updateWorkingHour;
                    applyLocalWorkingHourChange(updatedWorkingHoursResponse, WorkingHourOperation.UPDATE);
                    dispatch(updateWorkingHour(selectedBusinessId, updatedWorkingHoursResponse, WorkingHourOperation.UPDATE));
                })
                .catch((error) => {
                    console.error("Error updating working hour:", error);
                });
        } else {
            console.error("Parsed time is invalid:", parsedTime);
        }
    };


    const validateTimeRange = (parent, children, newOrUpdatedRecord) => {
        const errors = [];
        const mergedRecords = [];

        if (parent) {
            mergedRecords.push({ ...parent });
        }
        children.forEach((child) => {
            mergedRecords.push({ ...child });
        });
        if (newOrUpdatedRecord) {
            const existingRecordIndex = mergedRecords.findIndex(
                (record) => parseInt(record.id) === newOrUpdatedRecord.id
            );
            if (existingRecordIndex === -1) {
                mergedRecords.push({ ...newOrUpdatedRecord });
            } else {
                mergedRecords[existingRecordIndex].startTime = newOrUpdatedRecord.startTime;
                mergedRecords[existingRecordIndex].endTime = newOrUpdatedRecord.endTime;
            }
        }

        for (let i = 1; i < mergedRecords.length; i++) {
            const prevRecord = mergedRecords[i - 1];
            const currRecord = mergedRecords[i];

            const prevEnd = convertTimeToMinutes(prevRecord.endTime);
            const currStart = convertTimeToMinutes(currRecord.startTime);
            const currEnd = convertTimeToMinutes(currRecord.endTime);

            if (currStart <= prevEnd && currStart >= currEnd) {
                errors.push({
                    id: currRecord.id,
                    message: `Conflict detected between ID ${prevRecord.id} (endTime: ${prevRecord.endTime}) and ID ${currRecord.id} (startTime: ${currRecord.startTime})`,
                });
            }
        }

        return errors;
    };

    const handleValidate = (parent, children, newRow, setTimeRangeErrors, setHasErrors) => {
        const errors = validateTimeRange(parent, children, newRow);
        if (errors.length > 0) {
            setTimeRangeErrors(errors);
            setHasErrors(true);
        } else {
            setTimeRangeErrors([]);
            setHasErrors(false);
        }
        return errors;
    };



    const handleAddRow = (parent, children, userTimeZone) => {
        const tzContext = resolveTimeZoneContext(userTimeZone);
        const parentId = Number(parent.id);
        let latestEndTime;
        if (children.length === 0) {
            latestEndTime = parent.endTime;
        } else {
            const lastChild = children[children.length - 1];
            latestEndTime = lastChild.endTime;
        }
        if (!latestEndTime || !/am|pm/i.test(latestEndTime)) {
            console.error("Invalid latest end time format:", latestEndTime);
            return;
        }
        latestEndTime = latestEndTime.trim().toLowerCase();
        const endTimeInMinutes = convertTimeToMinutes(latestEndTime);
        if (endTimeInMinutes === null || endTimeInMinutes < 0) {
            console.error("Error converting latestEndTime to minutes:", latestEndTime);
            return;
        }
        const newStartTimeInMinutes = endTimeInMinutes + 60;
        const newEndTimeInMinutes = newStartTimeInMinutes + 60;
        const newStartTime = formatTimeWithPeriod(newStartTimeInMinutes);
        const newEndTime = formatTimeWithPeriod(newEndTimeInMinutes);
        const newRow = createNewChildRow(parentId, parent.groupId, newStartTime, newEndTime);
        const errors = handleValidate(parent, children, newRow, setTimeRangeErrors, setHasErrors);
        if (errors.length > 0) {
            console.error("Error adding working hour:", "Validation failed");
            return;
        }
        addWorkingHourData({ variables: { input: newRow, timeZoneId: tzContext.id } })
            .then((res) => {
                let updatedWorkingHoursResponse = res.data?.addWorkingHour;
                if (updatedWorkingHoursResponse && typeof updatedWorkingHoursResponse === "object") {
                    const { __typename, ...rest } = updatedWorkingHoursResponse;
                    updatedWorkingHoursResponse = rest;
                }
                applyLocalWorkingHourChange(updatedWorkingHoursResponse, WorkingHourOperation.ADD);
                updateWorkingHour(selectedBusinessId, updatedWorkingHoursResponse, WorkingHourOperation.ADD);
            })
            .catch((error) => {
                console.error("Error adding working hour:", error);
            });
    };

    const updateWorkingHour = (
        selectedBusinessId,
        updatedWorkHour, // object or array of objects
        operation
    ) => {
        if (props.entityType === EntityType.BUSINESS) {
            dispatch(
                updateBusinessWorkingHour(
                selectedBusinessId,
                updatedWorkHour,
                operation
            )
            );
        } else if (props.entityType === EntityType.STAFF) {
            const normalizedHours = Array.isArray(updatedWorkHour)
                ? updatedWorkHour.filter(Boolean)
                : updatedWorkHour
                ? [updatedWorkHour]
                : [];
            if (!normalizedHours.length) return;

            const targetGroupId = Number(normalizedHours[0]?.groupId);
            const targetStaffId = resolveStaffIdForGroup(targetGroupId);
            const targetStaff = (selectedBusiness?.businessStaffs || []).find(
                (staff) => Number(staff?.id) === Number(targetStaffId)
            );
            const targetGroup =
                (targetStaff?.workingHourGroups || []).find(
                    (group) => Number(group?.id) === Number(targetGroupId)
                ) ||
                (workingHourGroup && Number(workingHourGroup?.id) === Number(targetGroupId)
                    ? workingHourGroup
                    : null);

            dispatch(
                updateStaffWorkingHour(
                selectedBusinessId,
                targetStaffId,
                    normalizedHours,
                targetGroup || {},
                operation,
            )
            );
        }
    };


    const createNewChildRow = (parentId, groupId, startTime, endTime) => ({
        startTime: startTime.trim().toLowerCase().replace(/\s{2,}/g, " "),
        endTime: endTime.trim().toLowerCase().replace(/\s{2,}/g, " "),
        onJob: true,
        parentId: parentId,
        groupId: groupId,
    });

    const handleDeleteRow = (child) => {
        deleteWorkingHourData({
            variables: { workingHourId: child.id },
        })
            .then((res) => {
                const result = res.data.deleteWorkingHour;
                if (result.success) { 
                } else {
                    console.log("Deletion failed:", result.message);
                }
                if (result.success) {
                    applyLocalWorkingHourChange(child, WorkingHourOperation.DELETE);
                    updateWorkingHour(selectedBusinessId, child, WorkingHourOperation.DELETE);
                }
            })
            .catch((error) => {
                if (error.response && error.response.errors) {
                    error.response.errors.forEach((graphQLError) => {
                        console.error("GraphQL Error:", graphQLError.message);
                    });
                }
            });
    };

    const copyWorkingHoursToDays = async (sourceParentId, targetDays, selectedTimeZone, sourceSnapshot) => {
        const rows = Array.isArray(workingHourGroup?.workingHours) ? workingHourGroup.workingHours : [];
        const isValidTime = (value) => {
            const v = String(value || "").trim().toLowerCase();
            return /^([01]?\d|2[0-3]):[0-5]\d$/.test(v) || /^(0?[1-9]|1[0-2]):[0-5]\d\s?(am|pm)$/.test(v);
        };
        const pickTime = (preferred, fallback) => {
            if (isValidTime(preferred)) return preferred;
            if (isValidTime(fallback)) return fallback;
            return "";
        };
        const sourceParent = rows.find(
            (row) =>
                Number(row?.id) === Number(sourceParentId) &&
                (row?.parentId === null || row?.parentId === undefined || row?.parentId === "")
        );
        if (!sourceParent) throw new Error("Source working hours are unavailable. Refresh and try again.");

        const sourceParentSnapshot = sourceSnapshot?.parent || null;
        const sourceChildrenSnapshot = Array.isArray(sourceSnapshot?.children) ? sourceSnapshot.children : null;
        const sourceChildrenFromRows = rows.filter((row) => Number(row?.parentId) === Number(sourceParentId));
        const sourceChildren = sourceChildrenSnapshot
            ? sourceChildrenSnapshot.map((child) => {
                const matchingRow = sourceChildrenFromRows.find((r) => Number(r?.id) === Number(child?.id));
                return {
                    ...matchingRow,
                    ...child,
                    startTime: pickTime(child?.startTime, matchingRow?.startTime),
                    endTime: pickTime(child?.endTime, matchingRow?.endTime),
                };
            })
            : sourceChildrenFromRows;
        const sourceStartTime = pickTime(sourceParentSnapshot?.startTime, sourceParent?.startTime);
        const sourceEndTime = pickTime(sourceParentSnapshot?.endTime, sourceParent?.endTime);
        if (!sourceStartTime || !sourceEndTime) throw new Error("The source working hours are invalid.");
        const sourceDay = String(sourceParent?.day || "").trim().toLowerCase();
        const normalizeDay = (day) => String(day || "").trim().toLowerCase();
        const tzContext = resolveTimeZoneContext(selectedTimeZone);

        for (const day of targetDays || []) {
            if (!day) continue;
            if (normalizeDay(day) === sourceDay) continue;

            const targetParent = rows.find(
                (row) =>
                    (row?.parentId === null || row?.parentId === undefined || row?.parentId === "") &&
                    normalizeDay(row?.day) === normalizeDay(day)
            );
            if (!targetParent) throw new Error(`Working hours for ${day} are unavailable.`);

            const targetChildren = rows.filter((row) => Number(row?.parentId) === Number(targetParent.id));
            // 1) Clear existing target child ranges first to avoid parent update validation conflicts.
            for (const child of targetChildren) {
                try {
                    const delRes = await deleteWorkingHourData({
                        variables: { workingHourId: child.id },
                    });
                    if (delRes?.data?.deleteWorkingHour?.success) {
                        applyLocalWorkingHourChange(child, WorkingHourOperation.DELETE);
                        updateWorkingHour(selectedBusinessId, child, WorkingHourOperation.DELETE);
                    } else {
                        throw new Error("Could not replace an existing time entry.");
                    }
                } catch (error) {
                    console.error("Error deleting target child during copy:", error);
                    throw error;
                }
            }

            // 2) Update target parent to source main range.
            let updatedTargetParentId = Number(targetParent.id);
            try {
                const updatedParent = {
                    ...targetParent,
                    onJob: sourceParent.onJob,
                    startTime: sourceStartTime,
                    endTime: sourceEndTime,
                    id: Number(targetParent.id),
                    groupId: Number(targetParent.groupId),
                    parentId: null,
                };
                const { __typename, ...parentInput } = updatedParent;
                const parentRes = await updateWorkingHourData({
                    variables: { input: parentInput, timeZoneId: tzContext.id },
                });
                const parentUpdatedRaw = parentRes?.data?.updateWorkingHour;
                const parentUpdated = normalizeWorkingHourRecord(parentUpdatedRaw, updatedParent);
                if (parentUpdated) {
                    updatedTargetParentId = Number(parentUpdated.id) || updatedTargetParentId;
                    applyLocalWorkingHourChange(parentUpdated, WorkingHourOperation.UPDATE);
                    updateWorkingHour(selectedBusinessId, parentUpdated, WorkingHourOperation.UPDATE);
                } else {
                    throw new Error("Could not copy the main time entry.");
                }
            } catch (error) {
                console.error("Error updating target parent during copy:", error);
                throw error;
            }

            // 3) Add copied child ranges under the target parent.
            for (const sourceChild of sourceChildren) {
                try {
                    const newChild = {
                        startTime: pickTime(sourceChild?.startTime, sourceStartTime),
                        endTime: pickTime(sourceChild?.endTime, sourceEndTime),
                        onJob: sourceChild.onJob,
                        parentId: Number(updatedTargetParentId),
                        groupId: Number(targetParent.groupId),
                    };
                    if (!newChild.startTime || !newChild.endTime) continue;
                    const addRes = await addWorkingHourData({
                        variables: { input: newChild, timeZoneId: tzContext.id },
                    });
                    const added = addRes?.data?.addWorkingHour;
                    if (added) {
                        applyLocalWorkingHourChange(added, WorkingHourOperation.ADD);
                        updateWorkingHour(selectedBusinessId, added, WorkingHourOperation.ADD);
                    } else {
                        throw new Error("Could not copy an additional time entry.");
                    }
                } catch (error) {
                    console.error("Error adding copied child during copy:", error);
                    throw error;
                }
            }
        }

        setTimeRangeErrors([]);
        setHasErrors(false);
    };

    const handleDeleteWorkingHourGroup = async (groupId) => {
        let confirmation;
        confirmation = await confirm({
            description: "Are you sure you want to delete this working hour group?",
        });

        if (confirmation.confirmed) {
            try {
                await deleteWorkingHourGroupData({ variables: { id: groupId } });
                refetchOverrides && refetchOverrides();
            } catch (err) {
                console.error("Error deleting working hour group:", err);
            }
        }
    };

    const convert24HourTo12Hour = (time24h, userTimeZone) => {
        try {
            const tzName =
                typeof userTimeZone === "string"
                    ? userTimeZone
                    : userTimeZone?.timezone || "UTC";
            const localTime = DateTime.fromFormat(time24h, "HH:mm", {
                zone: tzName,
            });
            if (!localTime.isValid) {
                throw new Error("Invalid time or timezone format.");
            }
            const time12h = localTime.toFormat("hh:mm a"); 
            return time12h;
        } catch (error) {
            console.error("Error converting time:", error.message);
            return null;
        }
    };

    // New functionality: update the working hours row in place.
    // It accepts an updated working hours array, then calls handleUpdateWorkingHourGroup with the current workingHourGroup.
    const updateWorkingHoursRow = (updatedWorkingHours) => {
        if (workingHourGroup && workingHourGroup.timeZone) {
            setWorkingHourGroup((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    workingHours: updatedWorkingHours,
                };
            });
            setTimeRangeErrors([]);
            setHasErrors(false);
            handleUpdateWorkingHourGroup(workingHourGroup, updatedWorkingHours, workingHourGroup.timeZone);
        }
    };

    return {
        workingHourGroup,
        hasErrors,
        handleChange,
        handleAddRow,
        handleDeleteRow,
        handleDeleteWorkingHourGroup,
        timeRangeErrors,
        getWorkingHours,
        getWorkingHourHolidays,
        getWorkingHourOverrides,
        handleTimezoneSelect,
        workingHourOverrideGroups,
        refetchOverrides,
        updateWorkingHoursRow, // New function for updating working hours rows
        copyWorkingHoursToDays,
    };
};
